import { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import { User } from '@features/file-explorer/types'
import {
  PermissionsData,
  PermissionLevel,
  UserOption,
  PermissionChange,
  Permission
} from './types'
import { formatUserName } from './UserSelectComponents'

export function usePermissionsModal(isOpen: boolean, item: any, onClose: () => void) {
  // Loading state
  const [isLoading, setIsLoading] = useState(false)
  const [permissionsData, setPermissionsData] = useState<PermissionsData | null>(null)

  // State for owner transfer
  const [newOwner, setNewOwner] = useState<UserOption | null>(null)
  const [previousOwnerAccess, setPreviousOwnerAccess] = useState<PermissionLevel>('admin')

  // State for permissions management
  const [permissions, setPermissions] = useState<Map<string, PermissionLevel>>(new Map())
  const [permissionsToRemove, setPermissionsToRemove] = useState<Set<string>>(new Set())
  const [newlyAddedUsers, setNewlyAddedUsers] = useState<Map<string, User>>(new Map())
  const [isSaving, setIsSaving] = useState(false)

  // Fetch permissions data when modal opens
  useEffect(() => {
    if (isOpen && item) {
      setIsLoading(true)
      const endpoint = item.is_folder
        ? `/folders/${item.id}/permissions`
        : `/documents/${item.id}/permissions`

      fetch(endpoint, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
        .then(res => res.json())
        .then(data => {
          setPermissionsData(data)
          // Initialize permissions map
          const permMap = new Map<string, PermissionLevel>(
            data.current_permissions.map((p: Permission) => [
              p.user.id,
              p.permission_level as PermissionLevel
            ])
          )
          setPermissions(permMap)
        })
        .catch(err => console.error('Error fetching permissions:', err))
        .finally(() => setIsLoading(false))
    }
  }, [isOpen, item])

  // Load users for React-Select
  const loadUsers = async (inputValue: string): Promise<UserOption[]> => {
    if (inputValue.length < 2) {
      return []
    }

    try {
      const response = await fetch(`/users/search?q=${encodeURIComponent(inputValue)}`)
      const data = await response.json()

      return data.users.map((user: User) => ({
        value: user.id,
        label: formatUserName(user),
        user
      }))
    } catch (error) {
      console.error('Error loading users:', error)
      return []
    }
  }

  // Handle adding a new user permission
  const handleAddUser = (option: UserOption | null) => {
    if (!option) return

    // Add user with 'read' permission by default
    setPermissions(prev => new Map(prev).set(option.user.id, 'read'))

    // Track newly added user
    setNewlyAddedUsers(prev => new Map(prev).set(option.user.id, option.user))

    // Remove from removal list if it was there
    setPermissionsToRemove(prev => {
      const newSet = new Set(prev)
      newSet.delete(option.user.id)
      return newSet
    })
  }

  // Handle permission level change
  const handlePermissionChange = (userId: string, level: PermissionLevel) => {
    setPermissions(prev => new Map(prev).set(userId, level))
  }

  // Handle removing a user permission
  const handleRemoveUser = (permissionId: string | number, userId: string) => {
    // Mark for removal (only if it's an existing permission)
    if (permissionId) {
      setPermissionsToRemove(prev => new Set(prev).add(String(permissionId)))
    }

    // Remove from permissions map
    setPermissions(prev => {
      const newMap = new Map(prev)
      newMap.delete(userId)
      return newMap
    })

    // Remove from newly added users
    setNewlyAddedUsers(prev => {
      const newMap = new Map(prev)
      newMap.delete(userId)
      return newMap
    })
  }

  // Handle save all changes
  const handleSaveChanges = () => {
    if (!item || !permissionsData) return

    setIsSaving(true)

    // Build permissions array (only non-removed permissions)
    const permissionsArray: PermissionChange[] = Array.from(permissions.entries())
      .map(([user_id, permission_level]) => ({
        user_id,
        permission_level
      }))

    // Build request data
    const requestData: any = {
      permissions: permissionsArray,
      remove_permission_ids: Array.from(permissionsToRemove)
    }

    // Add owner transfer data if new owner selected
    if (newOwner) {
      requestData.new_owner_id = newOwner.value
      requestData.previous_owner_access = previousOwnerAccess
    }

    // Determine endpoint based on resource type
    const endpoint = item.is_folder
      ? `/folders/${item.id}/permissions`
      : `/documents/${item.id}/permissions`

    router.patch(endpoint, requestData, {
      preserveScroll: true,
      onSuccess: () => {
        handleClose()
      },
      onError: (errors) => {
        console.error('Error updating permissions:', errors)
      },
      onFinish: () => {
        setIsSaving(false)
      }
    })
  }

  const handleClose = () => {
    if (!isSaving) {
      setPermissionsData(null)
      setNewOwner(null)
      setPreviousOwnerAccess('admin')
      setPermissions(new Map())
      setPermissionsToRemove(new Set())
      setNewlyAddedUsers(new Map())
      onClose()
    }
  }

  // Get all users with permissions (including newly added, excluding removed)
  const existingPermissionUsers = permissionsData?.current_permissions
    .filter(p => !permissionsToRemove.has(p.id))
    .map(p => ({
      permissionId: p.id,
      user: p.user,
      currentLevel: permissions.get(p.user.id) || p.permission_level as PermissionLevel,
      inherited: p.inherited
    })) || []

  // Add newly added users
  const newUsers = Array.from(newlyAddedUsers.entries()).map(([userId, user]) => ({
    permissionId: 0, // New users don't have a permission ID yet
    user,
    currentLevel: permissions.get(userId) || 'read' as PermissionLevel,
    inherited: false
  }))

  const allPermissionUsers = [...existingPermissionUsers, ...newUsers]

  return {
    isLoading,
    isSaving,
    permissionsData,
    newOwner,
    setNewOwner,
    previousOwnerAccess,
    setPreviousOwnerAccess,
    permissions,
    allPermissionUsers,
    loadUsers,
    handleAddUser,
    handlePermissionChange,
    handleRemoveUser,
    handleSaveChanges,
    handleClose
  }
}
